import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, User, Lock, Chrome, CircleAlert as AlertCircle } from 'lucide-react';
import { SupabaseAuth } from '../utils/supabaseAuth';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export const LoginPage: React.FC = () => {
  const { isAuthenticated, signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        console.log('[LOGIN] 🔍 Checking Supabase connection...');
        console.log('[LOGIN] Supabase URL:', import.meta.env.VITE_SUPABASE_URL);

        const { error } = await supabase.from('usuarios').select('count', { count: 'exact', head: true });

        if (error) {
          console.error('[LOGIN] ❌ Connection check failed:', error);
          console.error('[LOGIN] Error code:', error.code);
          console.error('[LOGIN] Error message:', error.message);
          setConnectionError(true);
        } else {
          console.log('[LOGIN] ✅ Connection successful');
          setConnectionError(false);
        }
      } catch (err) {
        console.error('[LOGIN] ❌ Connection error:', err);
        console.error('[LOGIN] Error details:', {
          name: err instanceof Error ? err.name : 'Unknown',
          message: err instanceof Error ? err.message : String(err),
        });
        setConnectionError(true);
      }
    };

    checkConnection();
  }, []);

  // Handle URL parameters for errors and success
  React.useEffect(() => {
    const error = searchParams.get('error');
    const login = searchParams.get('login');
    
    if (error) {
      // Decode the error message
      const decodedError = decodeURIComponent(error);
      
      if (error === 'google_auth_failed') {
        toast.error('Falha na autenticação com Google. Tente novamente.');
      } else if (error === 'authentication_failed') {
        toast.error('Usuário não encontrado ou não autorizado.');
      } else if (error === 'google_not_configured') {
        toast.error('Login com Google não está configurado. Use email e senha.');
      } else if (decodedError.includes('não está autorizado')) {
        toast.error('Seu email não está autorizado no sistema. Contate o administrador.');
      } else {
        toast.error(`Erro de autenticação: ${decodedError}`);
      }
    }
    
    if (login === 'success') {
      toast.success('Login realizado com sucesso!');
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleGoogleLogin = async () => {
    console.log('[LOGIN-PAGE] 🔐 Initiating Google OAuth flow...');
    console.log('[LOGIN-PAGE] Current origin:', window.location.origin);
    console.log('[LOGIN-PAGE] Redirect will be:', `${window.location.origin}/auth/callback`);

    try {
      const result = await SupabaseAuth.signInWithGoogle();
      console.log('[LOGIN-PAGE] ✅ OAuth initiated successfully:', result);
      toast.loading('Redirecionando para Google...', { duration: 3000 });
    } catch (error) {
      console.error('[LOGIN-PAGE] ❌ Google login error:', error);
      console.error('[LOGIN-PAGE] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
      });

      const errorMsg = error instanceof Error ? error.message : 'Erro ao iniciar login com Google';

      if (errorMsg.includes('conectar ao servidor')) {
        toast.error('Servidor de autenticação indisponível. Por favor, use login com email e senha ou tente novamente mais tarde.');
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleManualLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('[LOGIN-PAGE] 🔐 Manual login attempt for:', username);
    
    if (!username || !password) {
      toast.error('Preencha todos os campos!');
      return;
    }

    setLoading(true);
    
    try {
      console.log('[LOGIN-PAGE] 📡 Calling signIn...');
      const user = await signIn(username.trim(), password.trim());
      
      if (user) {
        console.log('[LOGIN-PAGE] ✅ Login successful for:', user.name);
        toast.success('Login realizado com sucesso!');
        // The context will handle the redirect automatically
      } else {
        console.log('[LOGIN-PAGE] ❌ Login failed - invalid credentials');
        toast.error('Usuário ou senha inválidos');
      }
      
    } catch (error) {
      console.error('[LOGIN-PAGE] ❌ Login error:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (errorMessage.includes('Usuário ou senha inválidos')) {
        toast.error('Usuário ou senha inválidos.');
      } else if (errorMessage.includes('Usuário não encontrado')) {
        toast.error('Usuário não encontrado no sistema. Contate o administrador.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        toast.error('Erro de conexão com o servidor. Verifique sua conexão com a internet.');
      } else {
        toast.error(errorMessage || 'Erro durante o login. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#1b3b69] dark:bg-gray-900">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img
                src="/gcf_logo_01.png"
                alt="Grupo Cropfield"
                className="h-20 w-auto"
              />
            </div>
            <h1 className="font-display text-headline-lg text-on-surface dark:text-gray-100 mb-2">Bem-vindo!</h1>
            <p className="text-secondary dark:text-gray-400">Faça login para acessar a Intranet</p>
          </div>

          {connectionError && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-error mb-1">
                    ⚠️ Servidor Indisponível
                  </p>
                  <p className="text-xs text-error mb-2">
                    Seu navegador não consegue conectar ao servidor. Verifique sua conexão.
                  </p>
                  <p className="text-xs text-error mb-2">
                    <strong>Possíveis causas:</strong>
                  </p>
                  <ul className="text-xs text-error list-disc list-inside space-y-1 mb-2">
                    <li>Cache do navegador desatualizado</li>
                    <li>DNS local com informações antigas</li>
                    <li>Projeto Supabase pausado</li>
                  </ul>
                  <p className="text-xs text-error">
                    <strong>Soluções:</strong>
                  </p>
                  <ol className="text-xs text-error list-decimal list-inside space-y-1">
                    <li>Limpe o cache do navegador (Ctrl+Shift+Delete)</li>
                    <li>Tente em modo anônimo (Ctrl+Shift+N)</li>
                    <li>Consulte SUPABASE-CONNECTION-FIX.md</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            disabled={connectionError}
            className="w-full mb-6 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Chrome className="w-5 h-5" />
            <span className="font-medium">Entrar com Google</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-outline-variant" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-secondary dark:text-gray-400">ou</span>
            </div>
          </div>

          {/* Manual Login Form */}
          <form onSubmit={handleManualLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-on-surface dark:text-gray-200 mb-2">
                Usuário
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary w-5 h-5" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-outline-variant dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700 text-on-surface dark:text-gray-200"
                  placeholder="Digite seu usuario"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface dark:text-gray-200 mb-2">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-outline-variant dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700 text-on-surface dark:text-gray-200"
                  placeholder="Digite sua senha"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary hover:text-on-surface"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-secondary dark:text-gray-400">
              Em caso de problemas, entre em contato com o setor de TI.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};